import { keepPreviousData, useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import { AssetApi, AssetInfo, Configuration } from '@/cip/_openapi/portfolio-optimizer';
import { defaultApiConfig } from '@/cip/api/defaultApiConfig';
import { MarketDataApi } from 'API';

const assetApi = new AssetApi(new Configuration(defaultApiConfig));

export const useNDVR1500Stocks = (options?: { refetchOnMount?: boolean; refetchOnWindowFocus?: boolean }) => {
    return useQuery({
        queryKey: ['NDVR1500Stocks'],
        queryFn: async () => {
            const [response] = await MarketDataApi().getNDVR1500Stocks();
            return response ?? undefined;
        },
        ...options,
    });
};

/**
 * Resolves share class ids to {@link AssetInfo}, keyed by scId. Fetches only the
 * requested share classes via the targeted asset API.
 */
export const useAssetsByScIdsMap = (scIds: string[]): { data: Map<string, AssetInfo>; isLoading: boolean } => {
    const { data: assets, isLoading } = useQuery({
        queryKey: ['asset', 'byScId', [...scIds].sort()],
        queryFn: () =>
            scIds.length === 0
                ? []
                : assetApi.getAssetsByScId({
                      requestBody: scIds as any,
                  }),
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });

    const data = useMemo(
        () => new Map((assets ?? []).filter((asset) => asset.scId).map((asset) => [asset.scId!, asset])),
        [assets],
    );

    return { data, isLoading };
};

/**
 * Hook to fetch asset information by security IDs.
 * Uses the targeted asset API to fetch only the requested securities.
 * Returns AssetInfo array with fields: sId, scId, symbol, description, assetClass, assetType.
 */
export const useAssetsByIds = (sIds: string[], options?: { throwOnError?: boolean }) => {
    return useQuery({
        ...options,
        queryKey: ['asset', 'bySId', [...sIds].sort()],
        queryFn: () => {
            return sIds.length === 0
                ? []
                : assetApi.getAssetsBySId({
                      requestBody: sIds as any,
                  });
        },
        placeholderData: keepPreviousData,
        refetchOnWindowFocus: false,
        refetchOnMount: false,
    });
};

/**
 * Hook to fetch asset information by security IDs and return as a Map.
 * Returns a Map with sId as key for O(1) lookups.
 * Useful when you need to frequently look up assets by their security ID.
 */
export const useAssetsByIdsMap = (sIds: string[], options?: { throwOnError?: boolean }) => {
    const query = useAssetsByIds(sIds, options);

    const assetsMap = useMemo(() => new Map(query.data?.map((asset) => [asset.sId, asset]) || []), [query.data]);

    return {
        ...query,
        data: assetsMap,
    };
};

export const useSRICategories = (options?: {
    refetchOnMount?: boolean;
    refetchOnWindowFocus?: boolean;
    throwOnError?: boolean;
}) => {
    return useQuery({
        queryKey: ['SRICategories'],
        queryFn: async () => {
            const [response] = await MarketDataApi().getDwhSriCategories();
            return response ?? undefined;
        },
        ...options,
    });
};
